import { Ref } from 'vue'
import { Analysis, Partner } from 'www/vue_components/common/Analyses/types'
import useCartContentStore from 'www/vue_components/common/Analyses/stores/useCartContentStore'
import { addAnalysisToCart, deleteAnalysisFromCart } from 'www/vue_components/common/Analyses/api'
import { handleRequestError } from 'www/vue_components/common/Analyses/functions/handleRequestError'
import { changeAnalysesCountInHeader } from 'www/vue_components/common/Analyses/functions/changeAnalysesCountInHeader'
import { removeCartEmptyStateFromHeader } from 'www/vue_components/common/Analyses/functions/removeCartEmptyStateFromHeader'
import useCommonAnalysesStore from 'www/vue_components/common/Analyses/stores/useCommonStore'
import useLabsStore from 'www/vue_components/common/Analyses/components/LaboratoriesList/stores/useLabsStore'

type Opts = {
  isRequestDone: Ref<boolean>
  isAnalysisLoading: Ref<boolean>
}
export function useCartActions(opts: Opts) {
  const {
    isAnalysisLoading,
    isRequestDone,
  } = opts

  const cartContentStore = useCartContentStore()
  const commonAnalysesStore = useCommonAnalysesStore()
  const labsStore = useLabsStore()

  function getTimeoutForRequest(partner: Partner, analysis: Analysis, isInCart: boolean) {
    // Если запрос быстрый, то не дизейблим кнопку, чтобы избежать мигания
    // если больше 300 мс, то начинаем дизейблить кнопку минимум 500 мс
    const msForStartDisableCartButton = 300
    const msForShowDisablingCartButton = 500

    return setTimeout(() => {
      isAnalysisLoading.value = true

      setTimeout(() => {
        if (isRequestDone.value) {
          updateCartButtonState(partner, analysis, isInCart)
        }
      }, msForShowDisablingCartButton)
    }, msForStartDisableCartButton)
  }

  function updateCartButtonState(partner: Partner, analysisParam: Analysis, isInCart: boolean) {
    isAnalysisLoading.value = false

    const analysis = partner.analyses.find(analysis => analysis.id === analysisParam.id
      && analysis.biomaterial_id === analysisParam.biomaterial_id)

    if (!analysis) {
      return
    }

    analysis.is_in_cart = isInCart

    if (isInCart) {
      removeCartEmptyStateFromHeader()
    }
  }

  // eslint-disable-next-line no-undef
  function resetRequestState(timeoutId: NodeJS.Timeout | number | undefined) {
    if (!isAnalysisLoading.value) {
      clearTimeout(timeoutId)
    }

    isRequestDone.value = false
    isAnalysisLoading.value = false
  }

  async function handleAddAnalysisToCart(partner: Partner, analysis: Analysis) {
    if (cartContentStore.ifMaxAnalysesInCart) {
      cartContentStore.showMaxAnalysesLimitDialog()

      return
    }

    let timeoutId

    try {
      timeoutId = getTimeoutForRequest(partner, analysis, true)

      const response = await addAnalysisToCart(
        analysis.analysis_synonym_id,
        partner.slug,
        analysis.biomaterial_id,
      )

      isRequestDone.value = true
      isAnalysisLoading.value = false

      cartContentStore.setTotalAnalysesInCartCount(response.data.analyses_in_cart_count)

      updateCartButtonState(partner, analysis, true)
      changeAnalysesCountInHeader(response.data.analyses_in_cart_count)
    } catch (error) {
      handleRequestError({ error, description: 'Не удалось добавить анализ в корзину' }, '#use-vue-base-dialog')
    }

    resetRequestState(timeoutId)
  }

  async function handleDeleteAnalysisFromCart(partner: Partner, analysis: Analysis, isReturnCartContent: boolean) {
    let timeoutId

    try {
      timeoutId = getTimeoutForRequest(partner, analysis, false)

      const { data } = await deleteAnalysisFromCart({
        synonymId: analysis.analysis_synonym_id,
        biomaterialId: analysis.biomaterial_id,
        townTranslit: commonAnalysesStore.town.translit,
        isReturnCartContent,
        lpuId: labsStore.laboratory.id,
      })

      updateCartButtonState(partner, analysis, false)
      cartContentStore.setTotalAnalysesInCartCount(data.analyses_in_cart_count)

      resetRequestState(timeoutId)

      changeAnalysesCountInHeader(data.analyses_in_cart_count)

      return data
    } catch (error) {
      handleRequestError({ error, description: 'Не удалось удалить анализ из корзины' }, '#use-vue-base-dialog')
      resetRequestState(timeoutId)
    }
  }

  return {
    handleDeleteAnalysisFromCart,
    handleAddAnalysisToCart,
  }
}
